import * as F from './fixed'
import type { Fixed } from './fixed'
import * as T from './table'

/**
 * Tacos.
 *
 * Separado em duas camadas de propósito:
 *
 *   `CueParams`  — o que a FÍSICA lê. Entra no replay, é limitado no código.
 *   `CueNft`     — o item inteiro: parâmetros + procedência + cosmético.
 *
 * A separação importa porque cosmético NÃO pode influenciar a partida. Skin,
 * cor e rastro podem ser tão exclusivos quanto se queira: é ali que a
 * monetização é segura, porque não muda quem ganha.
 *
 * Quatro regras que valem para os parâmetros de física:
 *
 * 1. FAZEM PARTE DO REPLAY. Reproduzir a partida exige saber com que taco cada
 *    tacada foi dada.
 * 2. O SERVIDOR DERIVA do NFT que a carteira possui. O cliente nunca informa,
 *    senão qualquer um se declara dono de taco lendário.
 * 3. SÃO LIMITADOS NO CÓDIGO, com teto não configurável.
 * 4. NENHUM TACO DOMINA OS OUTROS. Para todo par, cada um é melhor em alguma
 *    coisa — sem isso, uma mesa com dinheiro vira "quem pagou mais ganha", e
 *    aí não é item, é vitória à venda. Quem garante são dois testes de
 *    dominância: um aqui, sobre os parâmetros, e um em `packages/tacos`, sobre
 *    os pontos que a raridade distribui.
 *
 * SOBRE A MIRA: ela NÃO é atributo de taco. Todo taco aponta na mesma
 * resolução — a do próprio formato de replay (`uint16`, ~0,0055°) — e nenhum
 * introduz erro aleatório. Com RNG o replay deixaria de reproduzir a partida e
 * a auditoria pública morreria junto.
 *
 * Até a v5 havia uma GRADE de mira, e o atributo `mira` do NFT escolhia a
 * finura dela. Foi removida na v6 porque produzia tacada impossível: a grade
 * só era aplicada ao executar o tiro, a linha-guia era desenhada com o ângulo
 * cru, e existia posição em que nenhuma casa da grade acertava a caçapa.
 */

export type CueParams = {
  /** Peso. Mais pesado transfere mais energia — e engrossa a mira. */
  massBps: number
  /** Autoridade de efeito: quanto de spin o taco imprime. */
  spinBps: number
  /**
   * Resquício da mira quantizada da v5, mantido no tipo por causa do FORMATO.
   *
   * Vale sempre 10_000 e a física não o lê. Ele ocupa dois dos dez bytes que o
   * replay grava por taco, e a v5 congelada precisa deles para reproduzir as
   * partidas dela — removê-lo forçaria uma versão nova de formato sem ganho
   * nenhum. `clampCue` o normaliza para o padrão, venha o que vier.
   */
  aimBps: number
  /**
   * Aderência do couro: quanto do efeito sobrevive ao rolamento.
   * Maior segura o efeito por mais tempo depois da tacada.
   */
  clothGripBps: number
  /**
   * Potência extra na QUEBRA apenas.
   *
   * Isola o taco de quebra do taco de jogo — distinção real na sinuca, e razão
   * concreta para alguém querer mais de um em vez de só o "melhor".
   */
  breakBonusBps: number
}

export const DEFAULT_CUE: CueParams = {
  massBps: 10_000,
  spinBps: 10_000,
  aimBps: 10_000,
  clothGripBps: 10_000,
  breakBonusBps: 10_000,
}

/**
 * Faixas permitidas. Estreitas de propósito: o melhor taco bate ~15% mais
 * forte. Perceptível, incapaz de decidir a partida.
 *
 * `aimBps` não tem faixa: a mira é a mesma para todos (ver `clampCue`).
 */
export const CUE_LIMITS = {
  minMassBps: 8_500,
  maxMassBps: 11_500,
  minSpinBps: 8_000,
  maxSpinBps: 13_000,
  minClothGripBps: 9_000,
  maxClothGripBps: 11_000,
  minBreakBonusBps: 10_000,
  maxBreakBonusBps: 11_500,
} as const

// ------------------------------------------------------------------ mira

/**
 * A mira, agora sem grade.
 *
 * Só normaliza o ângulo, para que a saída seja canônica — é ela que vai para o
 * replay, e dois ângulos que representam a mesma direção precisam gravar o
 * mesmo número.
 *
 * A função continua existindo, em vez de o chamador normalizar direto, porque
 * é o ponto onde a mira do taco ERA aplicada: mantê-la deixa o `replay.ts`
 * idêntico ao da v5 nesse trecho e o diff entre as duas físicas legível.
 */
export function quantizeAim(angle: Fixed, _cue: CueParams): Fixed {
  return F.normalizeAngle(angle)
}

// -------------------------------------------------------------- derivados

/** Taxa de decaimento do efeito, já com a aderência do taco. */
export function spinDecayFor(cue: CueParams): Fixed {
  const ajustado = Math.floor((T.SPIN_DECAY * cue.clothGripBps) / 10_000)
  // Nunca ≥ 1: efeito que não decai duraria a tacada inteira e a simulação
  // pararia de convergir.
  return Math.max(0, Math.min(F.ONE - 1, ajustado))
}

/** Multiplicador de velocidade da tacada, considerando se é a quebra. */
export function shotPowerBpsFor(cue: CueParams, isBreak: boolean): number {
  if (!isBreak) return cue.massBps
  return Math.floor((cue.massBps * cue.breakBonusBps) / 10_000)
}

// ---------------------------------------------------------------- limites

/**
 * Prende os atributos na faixa válida.
 *
 * Sempre chamado antes de simular, mesmo com valores vindos do servidor: um
 * bug em outro lugar não pode virar taco com o dobro da força. Entrada
 * inválida (NaN, Infinity) cai para o PADRÃO, nunca para o teto — senão mandar
 * lixo seria recompensado.
 */
export function clampCue(cue: Partial<CueParams>): CueParams {
  const prender = (valor: number | undefined, minimo: number, maximo: number): number => {
    if (valor === undefined || !Number.isFinite(valor)) return 10_000
    return Math.max(minimo, Math.min(maximo, Math.round(valor)))
  }

  return {
    massBps: prender(cue.massBps, CUE_LIMITS.minMassBps, CUE_LIMITS.maxMassBps),
    spinBps: prender(cue.spinBps, CUE_LIMITS.minSpinBps, CUE_LIMITS.maxSpinBps),
    // A mira é igual para todos: o campo existe pelo formato, não pela física.
    aimBps: 10_000,
    clothGripBps: prender(
      cue.clothGripBps,
      CUE_LIMITS.minClothGripBps,
      CUE_LIMITS.maxClothGripBps,
    ),
    breakBonusBps: prender(
      cue.breakBonusBps,
      CUE_LIMITS.minBreakBonusBps,
      CUE_LIMITS.maxBreakBonusBps,
    ),
  }
}

/** Converte basis points para multiplicador em ponto fixo. */
export function bpsToFixed(bps: number): Fixed {
  return Math.floor((bps * F.ONE) / 10_000)
}

export const isDefaultCue = (cue: CueParams): boolean =>
  (Object.keys(DEFAULT_CUE) as Array<keyof CueParams>).every((k) => cue[k] === DEFAULT_CUE[k])

// -------------------------------------------------------------------- NFT

/**
 * Versão do esquema de atributos.
 *
 * Sem isto, mudar a física quebraria todo replay antigo: uma partida de ontem
 * seria reproduzida com regras de hoje e daria outro resultado. Cada replay
 * guarda a versão, e o verificador usa a interpretação daquela época.
 */
export const CUE_SCHEMA_VERSION = 1

/**
 * O taco como item.
 *
 * `cosmetic` fica FORA de `params` de propósito: nada aqui pode influenciar a
 * física, e existe teste garantindo que trocar a skin não muda o resultado de
 * uma tacada.
 */
export type CueNft = {
  schemaVersion: number
  /** Endereço do mint na Solana. */
  mint: string
  /** Número de série dentro da coleção. Desempate e procedência. */
  serial: number
  /** Slot em que foi mintado. Sem `Date`: a chain é o relógio. */
  mintedAtSlot: number
  params: CueParams
  cosmetic: CueCosmetic
}

/** Puramente visual. Não entra na simulação nem no hash do replay. */
export type CueCosmetic = {
  name: string
  skin: string
  trail?: string
}

/**
 * Extrai os parâmetros de física de um NFT, já limitados.
 *
 * Ponto único de entrada: é aqui que um NFT com metadados adulterados vira
 * atributos seguros. Esquema desconhecido cai para o taco padrão em vez de
 * confiar em campos que talvez signifiquem outra coisa.
 */
export function paramsFromNft(nft: Pick<CueNft, 'schemaVersion' | 'params'>): CueParams {
  if (nft.schemaVersion !== CUE_SCHEMA_VERSION) return DEFAULT_CUE
  return clampCue(nft.params)
}

/**
 * Arquétipos de exemplo — ponto de partida para os atributos da coleção.
 *
 * Nenhum domina os outros. Essa é a propriedade a preservar quando novos tacos
 * forem desenhados, e existe teste que percorre todos os pares para garantir.
 */
export const CUE_ARCHETYPES = {
  padrao: DEFAULT_CUE,
  /** Quebra bem, bate forte, controla pouco. */
  pesado: {
    massBps: 11_500,
    spinBps: 9_000,
    aimBps: 10_000,
    clothGripBps: 9_500,
    breakBonusBps: 11_500,
  },
  /**
   * Segura a branca onde ela foi deixada, e bate fraco.
   *
   * Era "mira fino, bate fraco" até a v5. Com a mira igual para todos, o que
   * sobrava dele era o padrão com menos peso — dominado pelo padrão, o que o
   * teste de dominância recusa. Passou a ser o taco de CONTROLE: a maior
   * aderência do catálogo.
   */
  preciso: {
    massBps: 8_500,
    spinBps: 10_000,
    aimBps: 10_000,
    clothGripBps: 11_000,
    breakBonusBps: 10_000,
  },
  /**
   * Autoridade de efeito, potência mediana.
   *
   * Cedeu aderência ao `preciso` (11_000 → 10_500) para que os dois se
   * distingam: com a aderência empatada no teto, o `efeito` dominava o
   * `preciso` em tudo o que restou.
   */
  efeito: {
    massBps: 9_500,
    spinBps: 13_000,
    aimBps: 10_000,
    clothGripBps: 10_500,
    breakBonusBps: 10_000,
  },
} as const satisfies Record<string, CueParams>

/**
 * O identificador de um taco do catálogo.
 *
 * ESTES NOMES SÃO PERMANENTES. É por eles que um NFT aponta para o taco que
 * concede, e um NFT já emitido não tem como ser reescrito — renomear `preciso`
 * transformaria em pó todos os que apontam para ele. Acrescentar é livre;
 * renomear e remover, não.
 *
 * Os ATRIBUTOS, ao contrário, podem ser rebalanceados à vontade: o replay
 * grava os dez bytes de cada taco (ver `format.ts`, offsets 36 e 46), e não o
 * id. Uma partida antiga continua se provando com os números que valiam quando
 * ela foi jogada, sem maquinaria de época nenhuma.
 */
export type CueId = keyof typeof CUE_ARCHETYPES

export const CUE_IDS = Object.keys(CUE_ARCHETYPES) as CueId[]

export const cueIdValido = (valor: unknown): valor is CueId =>
  typeof valor === 'string' && (CUE_IDS as string[]).includes(valor)

/**
 * O código de cada taco no CABEÇALHO DO REPLAY (um byte por jogador).
 *
 * TÃO PERMANENTE QUANTO O NOME, e pelo mesmo motivo: um replay já liquidado
 * na chain diz "jogado com o taco 2", e se 2 passasse a ser outro taco a arte
 * de toda partida antiga trocaria. Taco novo ganha o PRÓXIMO número livre;
 * nunca se reaproveita o de um removido.
 *
 * Só a arte depende disto. A física lê os dez bytes de atributos, que
 * continuam no cabeçalho — um replay de código desconhecido ainda se prova.
 */
export const CUE_CODES: Record<CueId, number> = {
  padrao: 0,
  pesado: 1,
  preciso: 2,
  efeito: 3,
}

const CUE_BY_CODE = new Map<number, CueId>(
  (Object.entries(CUE_CODES) as [CueId, number][]).map(([id, c]) => [c, id]),
)

/** O taco de um código do replay, ou `undefined` se ninguém tem esse código. */
export const cueIdPorCodigo = (codigo: number): CueId | undefined => CUE_BY_CODE.get(codigo)

/**
 * Os atributos de um taco do catálogo, ou o padrão.
 *
 * Id desconhecido cai no padrão em vez de lançar: ele chega da rede e do
 * perfil, e um id que este servidor não conhece — versão antiga, NFT de uma
 * coleção futura — não pode derrubar a montagem de uma partida. O jogador joga
 * com o taco padrão e a partida acontece.
 */
export function tacoDoCatalogo(id: unknown): CueParams {
  return cueIdValido(id) ? CUE_ARCHETYPES[id] : DEFAULT_CUE
}
